import type { Actor, ImpersonationSession, NavGroup } from '@titan/shared';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { refreshTokens, type Tokens } from '../auth/cognito.js';
import { config } from '../config.js';
import { ApiError, apiFetch } from './api.js';

export interface MeResponse {
  actor: Actor;
  nav: NavGroup[];
  impersonation: {
    session: ImpersonationSession;
    tenantName: string;
    plan: string | null;
    moduleCount: number;
    persona: { name: string; email: string; role: string };
    blocked: { reason: 'suspended' | 'noActiveUser'; message: string; tenantName: string; plan: string } | null;
  } | null;
}

interface StoredSession {
  tokens?: Tokens;
  devActor?: string;
  impersonation?: { token: string; session: ImpersonationSession };
}

export type SessionStatus = 'loading' | 'anonymous' | 'ready';

export interface SessionContextValue {
  status: SessionStatus;
  me: MeResponse | null;
  /** Sign-in stopped at the API: the identity is valid but has no access. */
  refusal: string | null;
  isDev: boolean;
  api: <T>(path: string, init?: RequestInit) => Promise<T>;
  signInWithTokens: (tokens: Tokens) => Promise<void>;
  signInDev: (actor: string) => Promise<void>;
  signOut: () => void;
  reload: () => Promise<void>;
  startImpersonation: (token: string, session: ImpersonationSession) => Promise<void>;
  exitImpersonation: () => Promise<string | null>;
}

const STORAGE_KEY = 'titan.session';
const SessionContext = createContext<SessionContextValue | null>(null);

function readStored(): StoredSession {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as StoredSession) : {};
  } catch {
    return {};
  }
}

function writeStored(s: StoredSession): void {
  try {
    if (!s.tokens && !s.devActor) sessionStorage.removeItem(STORAGE_KEY);
    else sessionStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  } catch {
    // storage unavailable: the session lives in memory only
  }
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const stored = useRef<StoredSession>(readStored());
  const [status, setStatus] = useState<SessionStatus>('loading');
  const [me, setMe] = useState<MeResponse | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);

  const authHeaders = useCallback(async () => {
    const s = stored.current;
    if (s.tokens && s.tokens.expiresAt - Date.now() < 60_000 && s.tokens.refreshToken) {
      try {
        s.tokens = await refreshTokens(s.tokens);
        writeStored(s);
      } catch {
        stored.current = {};
        writeStored({});
        throw new ApiError(401, 'session_ended', 'Your session has ended. Sign in again.');
      }
    }
    return {
      ...(s.tokens ? { idToken: s.tokens.idToken } : {}),
      ...(s.devActor ? { devActor: s.devActor } : {}),
      ...(s.impersonation ? { impersonationToken: s.impersonation.token } : {}),
    };
  }, []);

  const api = useCallback(
    async <T,>(path: string, init?: RequestInit): Promise<T> => {
      const auth = await authHeaders();
      try {
        return await apiFetch<T>(path, auth, init);
      } catch (err) {
        if (err instanceof ApiError && err.status === 401 && path !== '/me') {
          stored.current = {};
          writeStored({});
          setMe(null);
          setStatus('anonymous');
        }
        throw err;
      }
    },
    [authHeaders],
  );

  const reload = useCallback(async () => {
    const s = stored.current;
    if (!s.tokens && !s.devActor) {
      setMe(null);
      setStatus('anonymous');
      return;
    }
    try {
      const next = await apiFetch<MeResponse>('/me', await authHeaders());
      setMe(next);
      setRefusal(null);
      setStatus('ready');
    } catch (err) {
      stored.current = {};
      writeStored({});
      setMe(null);
      setStatus('anonymous');
      if (err instanceof ApiError && err.status === 401) {
        setRefusal('Access to Titan is granted by your organization or by DinoCloud. Ask your administrator to add you.');
      }
    }
  }, [authHeaders]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const signInWithTokens = useCallback(
    async (tokens: Tokens) => {
      stored.current = { tokens };
      writeStored(stored.current);
      await reload();
    },
    [reload],
  );

  const signInDev = useCallback(
    async (actor: string) => {
      stored.current = { devActor: actor };
      writeStored(stored.current);
      await reload();
    },
    [reload],
  );

  const signOut = useCallback(() => {
    stored.current = {};
    writeStored({});
    setMe(null);
    setStatus('anonymous');
  }, []);

  const startImpersonation = useCallback(
    async (token: string, session: ImpersonationSession) => {
      stored.current = { ...stored.current, impersonation: { token, session } };
      writeStored(stored.current);
      await reload();
    },
    [reload],
  );

  const exitImpersonation = useCallback(async (): Promise<string | null> => {
    const tenantId = stored.current.impersonation?.session.tenantId ?? null;
    try {
      await api('/impersonation', { method: 'DELETE' });
    } catch {
      // the session may already have expired; leaving is still the right move
    }
    delete stored.current.impersonation;
    writeStored(stored.current);
    await reload();
    return tenantId;
  }, [api, reload]);

  const value = useMemo<SessionContextValue>(
    () => ({
      status,
      me,
      refusal,
      isDev: config.authMode === 'dev',
      api,
      signInWithTokens,
      signInDev,
      signOut,
      reload,
      startImpersonation,
      exitImpersonation,
    }),
    [status, me, refusal, api, signInWithTokens, signInDev, signOut, reload, startImpersonation, exitImpersonation],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used inside SessionProvider.');
  return ctx;
}

/** The signed-in actor. Only for components rendered behind RequireSession. */
export function useActor(): Actor {
  const { me } = useSession();
  if (!me) throw new Error('No session.');
  return me.actor;
}
